// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The workflow: folder → bundle → paste → review → write.
 *
 * THE ASSIGNMENT QUEUE IS THE PRODUCT, and the design follows from one
 * measurement: over 10,988 fenced blocks in real multi-block programming
 * answers, 3.82% carry a recoverable file path and 96.18% do not. unfence is
 * therefore not a path RECOVERY tool with an assignment fallback — it is a path
 * ASSIGNMENT tool with recovery as an accelerator. The queue, with typeahead
 * over the folder tree already enumerated, is the surface that has to be good.
 *
 * Everything the reply supplies is data. Paths from it are validated, contents
 * from it are shown before they are written, and instructions in it are ignored
 * — there is no code path from a parsed block to any setting in this file.
 */

import { buildBundle, buildSkipped, type SkippedEntry } from './engine/bundle';
import { compileGitignore, deadNegations, isIgnored, type IgnoreRule } from './engine/gitignore';
import { checkPath, collisionGroups } from './engine/pathsafe';
import { applyHunks, buildPatch, checkInvariants, type PatchInput } from './engine/patch';
import { checkSecret, findLeaks, parseEnvValues } from './engine/secrets';
import { byteLength, estimateBundle, formatTokens } from './engine/tokens';
import { stripPathHeader } from './engine/blocks';
import { toLf } from './engine/eol';
import type { FileChange, Hunk, ParsedBlock, SourceFile } from './types';
import { decode, type RawFile, type ReadResult } from './fs/read';
import { hashText, type WriteItem } from './fs/write';

export interface Project {
  readonly folderName: string;
  readonly handle: FileSystemDirectoryHandle | null;
  readonly files: SourceFile[];
  readonly rules: IgnoreRule[];
  readonly deadRules: IgnoreRule[];
  readonly truncated: boolean;
  /** Files excluded, with the reason, so the omission is never silent. */
  readonly omitted: { path: string; reason: string }[];
  readonly leaks: { path: string; variable: string | null }[];
  readonly hidDotfiles: boolean;
}

const encoder = new TextEncoder();

/**
 * Turn what a reader handed us into a project: decode, apply ignore rules,
 * refuse secrets.
 *
 * The gitignore layers are collected FIRST and sorted shallow-to-deep, because
 * gitignore(5) has lower-level files override higher ones and "last match wins"
 * is what implements that.
 */
export async function buildProject(
  result: ReadResult,
  onProgress: (done: number, total: number) => void,
  hidDotfiles: boolean,
): Promise<Project> {
  const byPath = new Map<string, RawFile>(result.files.map((f) => [f.path, f]));

  const ignoreSources: [string, string][] = [];
  for (const [path, raw] of byPath) {
    if (!path.endsWith('.gitignore')) continue;
    const base = path.slice(0, Math.max(0, path.length - '.gitignore'.length - 1));
    ignoreSources.push([base, await raw.file.text()]);
  }
  const rules = ignoreSources
    .sort(([a], [b]) => a.split('/').length - b.split('/').length || a.localeCompare(b))
    .flatMap(([base, text]) => compileGitignore(text, base));

  const files: SourceFile[] = [];
  const omitted: { path: string; reason: string }[] = [];
  let done = 0;

  for (const raw of result.files) {
    const decoded = await decode(raw);
    const ignored = isIgnored(rules, raw.path, false);
    // The head is only consulted for the two extensions where a private key and
    // a public certificate share a suffix.
    const head = decoded.text?.slice(0, 4096);
    const secret = checkSecret(raw.path, head);

    if (secret.refused) {
      omitted.push({ path: raw.path, reason: secret.reason ?? 'refused' });
      files.push({ ...decoded, ignored: true, refused: secret.reason });
    } else if (ignored.ignored) {
      omitted.push({ path: raw.path, reason: `ignored by ${ignored.rule?.source ?? '.gitignore'}` });
      files.push({ ...decoded, ignored: true });
    } else if (decoded.text === null) {
      omitted.push({ path: raw.path, reason: 'not text' });
      files.push({ ...decoded, ignored: true });
    } else {
      files.push(decoded);
    }

    done++;
    if (done % 25 === 0) {
      onProgress(done, result.files.length);
      // Yield so the progress actually paints on a large folder.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  onProgress(result.files.length, result.files.length);

  // Best-effort only, and labelled as such wherever it is shown: a build tool
  // replaces a value and THEN minifies, so the same secret can arrive split
  // across a template literal or re-encoded inside JSON, and a literal search
  // finds none of those.
  const envValues: { name: string | null; value: string }[] = [];
  for (const file of files) {
    if (!file.refused || !/^\.env($|\.)/i.test(file.path.split('/').pop() ?? '')) continue;
    const raw = byPath.get(file.path);
    if (!raw) continue;
    for (const value of parseEnvValues(await raw.file.text())) envValues.push({ name: null, value });
  }
  const leaks = envValues.length
    ? findLeaks(envValues, files.filter((f) => !f.ignored && f.text)).map((h) => ({
        path: h.path,
        variable: h.variable,
      }))
    : [];

  return {
    folderName: result.folderName,
    handle: result.handle,
    files,
    rules,
    deadRules: deadNegations(rules),
    truncated: result.truncated,
    omitted,
    leaks,
    hidDotfiles,
  };
}

export interface BundleSelection {
  /** Paths the user has explicitly excluded on top of the automatic rules. */
  readonly excluded: Set<string>;
}

export function includedFiles(project: Project, selection: BundleSelection): SourceFile[] {
  return project.files.filter((f) => !f.ignored && f.text !== null && !selection.excluded.has(f.path));
}

export function bundleParts(project: Project, selection: BundleSelection): string[] {
  const included = includedFiles(project, selection);
  const excluded = [
    ...project.omitted,
    ...[...selection.excluded].map((path) => ({ path, reason: 'you excluded it' })),
  ];
  return buildBundle(included, {
    folderName: project.folderName,
    omitted: excluded,
    generatedNote: 'Packed by unfence (unfence.benrichardson.dev). Nothing was uploaded to produce this file.',
  });
}

export function bundleSize(project: Project, selection: BundleSelection): { bytes: number; label: string } {
  const files = includedFiles(project, selection).map((f) => ({
    path: f.path,
    bytes: byteLength(f.text ?? ''),
  }));
  const estimate = estimateBundle(files);
  const bytes = files.reduce((sum, f) => sum + f.bytes, 0);
  return {
    bytes,
    label: `≈${formatTokens(estimate.tokens)} tokens (${formatTokens(estimate.low)}–${formatTokens(estimate.high)})`,
  };
}

/* ── the inbound half ─────────────────────────────────────────────────────── */

export interface Assignment {
  /** Path chosen by the user for a block the reply did not label. */
  readonly path: string | null;
  readonly overridden: boolean;
}

export interface Plan {
  readonly changes: FileChange[];
  readonly skipped: SkippedEntry[];
  readonly unassigned: ParsedBlock[];
  readonly collisions: string[][];
}

/**
 * What each block would become, before anything is written.
 *
 * Refusals go into `skipped` rather than being dropped: a tool that quietly
 * discards half its input looks identical to one that found nothing.
 */
export function planChanges(
  project: Project,
  blocks: readonly ParsedBlock[],
  assignments: ReadonlyMap<number, Assignment>,
): { jobs: { path: string; blockIndex: number; oldText: string; newText: string; kind: 'modify' | 'create' }[]; skipped: SkippedEntry[]; unassigned: ParsedBlock[]; collisions: string[][] } {
  const onDisk = new Map(project.files.map((f) => [f.path, f]));
  const skipped: SkippedEntry[] = [];
  const unassigned: ParsedBlock[] = [];
  const jobs: { path: string; blockIndex: number; oldText: string; newText: string; kind: 'modify' | 'create' }[] = [];

  const resolved: { block: ParsedBlock; path: string }[] = [];
  for (const block of blocks) {
    const assignment = assignments.get(block.index);
    const path = assignment?.path ?? block.path;

    if (block.truncated) {
      skipped.push({
        label: path ?? `Block ${block.index + 1}`,
        reason:
          'The reply was cut off part-way through this block — its closing fence is missing, so the file is incomplete.',
      });
      continue;
    }
    if (!path) {
      unassigned.push(block);
      continue;
    }
    resolved.push({ block, path });
  }

  /*
   * EXACT duplicates collapse here, keeping the LAST — the same rule the parser
   * applies to paths a reply labelled itself.
   *
   * It has to happen again at this level because a USER ASSIGNMENT can create a
   * duplicate the parser never saw: two unlabelled blocks, both placed at
   * `src/notes.md`. The browser dry-run produced exactly that, and the patch it
   * generated carried two `+++ b/src/notes.md` create stanzas — which `git
   * apply` refuses outright with "already exists in working directory". Keeping
   * the last matches what a regenerated block means, and it is what the user
   * sees: the second placement replaces the first.
   */
  const lastForPath = new Map<string, number>();
  for (const { block, path } of resolved) lastForPath.set(path, block.index);
  const deduped = resolved.filter(({ block, path }) => lastForPath.get(path) === block.index);
  for (const { block, path } of resolved) {
    if (lastForPath.get(path) === block.index) continue;
    skipped.push({
      label: path,
      reason: `Superseded — a later block was placed at the same path, and that one is what will be written.`,
    });
    void block;
  }

  // Case-fold collisions are refused as a GROUP. On APFS and NTFS `src/App.ts`
  // and `src/app.ts` are one file; on ext4 they are two. Merging is wrong on one
  // and not merging destroys a file on the other, and a web page cannot tell
  // which disk it is facing.
  const groups = collisionGroups(deduped.map((r) => r.path));
  const colliding = new Set([...groups.values()].flat());
  const collisions = [...groups.values()];

  for (const { block, path } of deduped) {
    if (colliding.has(path)) {
      skipped.push({
        label: path,
        reason:
          'The reply contains two paths that differ only in capitalisation. On macOS and Windows those are the ' +
          'same file, on Linux they are two — so writing either one could destroy the other. Both are refused.',
      });
      continue;
    }

    const verdict = checkPath(path);
    const override = assignments.get(block.index)?.overridden ?? false;
    if (!verdict.ok && !(verdict.overridable && override)) {
      skipped.push({ label: path, reason: verdict.reason ?? 'This path was refused.' });
      continue;
    }

    const existing = onDisk.get(path);
    // The source decides whether the label is metadata or part of the file.
    const content = stripPathHeader(block.content, path, assignments.get(block.index)?.path ? 'assigned' : block.pathSource);
    jobs.push({
      path,
      blockIndex: block.index,
      oldText: existing?.text ?? '',
      newText: content,
      kind: existing && existing.text !== null ? 'modify' : 'create',
    });
  }

  return { jobs, skipped, unassigned, collisions };
}

/** The bytes that would be written for one reviewed change. */
export function resultText(change: FileChange): string {
  return applyHunks(toLf(change.oldText), change.hunks);
}

export function writeItems(changes: readonly FileChange[]): WriteItem[] {
  return changes
    .filter((c) => c.hunks.some((h) => h.accepted))
    .filter((c) => c.verdict.ok || c.overridden)
    .filter((c) => !c.elision.elided || c.overridden)
    .map((c) => ({
      path: c.path,
      text: resultText(c),
      eol: c.eol,
      kind: c.kind,
      planHash: c.planHash,
    }));
}

export function patchInputs(changes: readonly FileChange[]): PatchInput[] {
  return changes
    .filter((c) => c.hunks.some((h) => h.accepted))
    .filter((c) => c.verdict.ok || c.overridden)
    .filter((c) => !c.elision.elided || c.overridden)
    .map((c) => ({
      path: c.path,
      kind: c.kind,
      oldText: toLf(c.oldText),
      newText: resultText(c),
      hunks: c.hunks,
    }));
}

export const PATCH_HEADER = [
  'Generated by unfence. Apply from the TOP LEVEL of your repository:',
  '',
  '    git apply changes.patch',
  '',
  'Run from a subdirectory and git quietly skips every path outside it.',
  'Never pass --unsafe-paths: it removes the protections that stop a patch',
  'writing to .git/hooks or outside the repository.',
];

export const APPLIED_HEADER = [
  'This is a RECORD of what unfence already wrote to your folder.',
  'Do NOT run `git apply` on it — the changes are already in place and',
  'applying it again would apply them twice.',
];

export function buildArtefacts(changes: readonly FileChange[], skipped: readonly SkippedEntry[], applied: boolean) {
  const patch = buildPatch(patchInputs(changes), applied ? APPLIED_HEADER : PATCH_HEADER);
  const invariants = checkInvariants(patch.text);
  const summary = applied
    ? `${changes.length} ${changes.length === 1 ? 'file was' : 'files were'} written; ${skipped.length} skipped.`
    : `${patch.files} ${patch.files === 1 ? 'file' : 'files'} in the patch; ${skipped.length} skipped.`;
  return {
    patch: patch.text,
    patchFiles: patch.files,
    invariants,
    skippedText: buildSkipped(skipped, summary),
  };
}

/** Every file about to be overwritten, zipped. Files being CREATED are not backed up. */
export function backupEntries(
  project: Project,
  items: readonly WriteItem[],
): { name: string; input: Uint8Array }[] {
  const onDisk = new Map(project.files.map((f) => [f.path, f]));
  const entries: { name: string; input: Uint8Array }[] = [];
  for (const item of items) {
    const existing = onDisk.get(item.path);
    if (!existing || existing.text === null) continue;
    entries.push({ name: item.path, input: encoder.encode(existing.text) });
  }
  return entries;
}

export function planHashFor(project: Project, path: string): string {
  const file = project.files.find((f) => f.path === path);
  return hashText(file?.text ?? '');
}

/** Accept or reject every hunk of a change at once. */
export function setAllHunks(hunks: Hunk[], accepted: boolean): void {
  for (const hunk of hunks) hunk.accepted = accepted;
}
